import React, { useState } from 'react';
import { isDebugMode } from '@sluitstraat/core';
import { workflowDebugPanelStyles as styles } from '../styles/commonStyles';

interface WorkflowDebugPanelProps {
  /** Aggregated current-page data, keyed by section (e.g. ids / relation / package / products / payment). Each value is dumped as JSON. */
  collected: Record<string, unknown>;
  /** Optional request payload — only shown on local dev (import.meta.env.DEV). */
  request?: unknown;
  /** Optional response payload — only shown on local dev. */
  response?: unknown;
  /** Header label. */
  title?: string;
}

// Shared debug panel for the application-flow screens (Payment / Acceptance / Confirmation).
// Unlike the per-product <DebugPanel> (single request/response), this dumps the COLLECTED
// workflow data on the page — the summary-endpoint responses plus added info (IBAN etc.) —
// as one labelled JSON form, with an optional request/response block for local dev only.
// Styles live in commonStyles (workflowDebugPanelStyles) — no CSS is defined in this file.
export const WorkflowDebugPanel: React.FC<WorkflowDebugPanelProps> = ({
  collected,
  request,
  response,
  title = 'Debug: Page Data',
}) => {
  const [open, setOpen] = useState(false);
  if (!isDebugMode()) return null;

  const showReqRes = import.meta.env.DEV && (request !== undefined || response !== undefined);

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <h3 style={styles.title}>{title}</h3>
        <button type="button" style={styles.toggle} onClick={() => setOpen(!open)}>
          {open ? '▼ Hide' : '▶ Show'}
        </button>
      </div>

      {open && (
        <div style={styles.content}>
          {Object.entries(collected).map(([section, value]) => (
            <div key={section} style={styles.section}>
              <h4 style={styles.sectionTitle}>{section}</h4>
              <pre style={styles.pre}>{JSON.stringify(value, null, 2)}</pre>
            </div>
          ))}

          {showReqRes && (
            <>
              {request !== undefined && (
                <div style={styles.section}>
                  <h4 style={styles.devSectionTitle}>Request (local dev)</h4>
                  <pre style={styles.pre}>{JSON.stringify(request, null, 2)}</pre>
                </div>
              )}
              {response !== undefined && (
                <div style={styles.section}>
                  <h4 style={styles.devSectionTitle}>Response (local dev)</h4>
                  <pre style={styles.pre}>{JSON.stringify(response, null, 2)}</pre>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkflowDebugPanel;
